import dayjs from 'dayjs';

import type { TicketCategory } from '../../operatorTickets/types';
import type { IStartWindow, IValidity } from '../types';

import {
	buildStartDates,
	computeValidityEndDate,
	isSelectableStartDate,
	weekHighlightRange,
} from './validity';

export interface IValidityOption {
	id: string; // stable react key
	startDate: string; // ISO; '' for the picker option
	labelKey: string; // i18n key for the row title
	labelParams?: Record<string, string>;
	opensPicker?: boolean; // true → tapping opens the calendar
	pickHintKey?: string; // i18n key for the hint shown when no date is picked yet
}

export interface IValidityPickerConfig {
	minDate: string;
	maxDate: string;
	/** Preselected date when the picker opens: the window's earliest start. */
	defaultPickerDate: string;
	isSelectable: (iso: string) => boolean;
	getHighlightRange?: (iso: string) => { start: string; end: string } | null;
}

export interface IValidityConfig {
	mode: 'options' | 'static';
	options: IValidityOption[]; // [] when static
	staticKeys?: { description: string; note: string }; // short-trip
	defaultStartDate: string | null; // first option's date / today / null (no window)
	picker?: IValidityPickerConfig; // present only when an option opensPicker
}

const PICK_OPTION: IValidityOption = {
	id: 'pick',
	startDate: '',
	labelKey: 'ticket_detail.validity_pick_date',
	opensPicker: true,
	pickHintKey: 'ticket_detail.validity_pick_hint_day',
};

/**
 * The two server-derived presets (earliest, earliest + 1) labelled in
 * order, followed by the calendar option. Empty without a start window.
 */
const buildPresetOptions = (
	dates: string[],
	presets: Pick<IValidityOption, 'id' | 'labelKey'>[]
): IValidityOption[] => {
	if (!dates.length) return [];
	const options = presets
		.slice(0, dates.length)
		.map((preset, i) => ({ ...preset, startDate: dates[i] }));
	return [...options, PICK_OPTION];
};

/** Daily options: Today (earliest), Tomorrow (earliest + 1 day), Choose date. */
export const buildDailyOptions = (window?: IStartWindow): IValidityOption[] =>
	buildPresetOptions(buildStartDates(window, 'day'), [
		{ id: 'today', labelKey: 'ticket_detail.validity_daily_today' },
		{ id: 'tomorrow', labelKey: 'ticket_detail.validity_daily_tomorrow' },
	]);

/** Weekly options: This week (earliest), Next week (earliest + 1 week), Choose date. */
export const buildWeeklyOptions = (window?: IStartWindow): IValidityOption[] =>
	buildPresetOptions(buildStartDates(window, 'week'), [
		{ id: 'this-week', labelKey: 'ticket_detail.validity_weekly_this_week' },
		{ id: 'next-week', labelKey: 'ticket_detail.validity_weekly_next_week' },
	]);

/**
 * Month-aligned options (Monthly ticket, Yearly ticket): this month (earliest),
 * next month (earliest + 1 month), or next+1 (latest). Only the row label
 * differs between the two ticket types.
 */
export const buildMonthOptions = (
	window: IStartWindow | undefined,
	labelKey: string
): IValidityOption[] =>
	buildStartDates(window, 'month').map((startDate) => ({
		id: startDate,
		startDate,
		labelKey,
		labelParams: { month: formatMonthName(startDate) },
	}));

/** "April" — month name in German via the global dayjs locale. */
export const formatMonthName = (startDate: string): string =>
	dayjs(startDate).format('MMMM');

/** "Sa, 14.03.2026" */
export const formatActiveFrom = (startDate: string): string =>
	startDate ? dayjs(startDate).format('dd, DD.MM.YYYY') : '';

/** End-of-validity display parts: weekday+date string and HH:mm time ('' if none). */
export const getValidUntilParts = (
	startDate: string,
	validity: Pick<IValidity, 'period' | 'periodCount' | 'validUntil'>
): { date: string; time: string } => {
	const end = computeValidityEndDate(
		startDate,
		validity.period,
		validity.periodCount
	);
	return {
		date: dayjs(end).format('dd, DD.MM.YYYY'),
		time: (validity.validUntil || '').slice(0, 5),
	};
};

/**
 * Date the calendar sheet opens on. A selection made via the picker (i.e. not
 * one of the preset option dates) is shown again; a preset selection
 * (Heute/Morgen/this week/next week) falls back to defaultPickerDate so the
 * picker always opens on the window's earliest start.
 */
export const getPickerOpenDate = (
	config: IValidityConfig,
	selectedStartDate: string | null
): string | null => {
	if (!config.picker) return null;

	const presetDates = config.options
		.filter((o) => !o.opensPicker)
		.map((o) => o.startDate);

	const isPickerSelection =
		selectedStartDate !== null && !presetDates.includes(selectedStartDate);

	return isPickerSelection
		? selectedStartDate
		: config.picker.defaultPickerDate;
};

/** Calendar config for day/week tickets: bounded by the server window. */
const buildPickerConfig = (
	period: 'day' | 'week',
	window: IStartWindow | undefined
): IValidityPickerConfig | undefined => {
	if (!window?.earliest || !window.latest) return undefined;
	const earliest = dayjs(window.earliest).format('YYYY-MM-DD');
	const latest = dayjs(window.latest).format('YYYY-MM-DD');
	return {
		minDate: earliest,
		maxDate: latest,
		defaultPickerDate: earliest,
		isSelectable: (iso) => isSelectableStartDate(iso, { period, window }),
		...(period === 'week'
			? { getHighlightRange: (iso: string) => weekHighlightRange(iso) }
			: {}),
	};
};

/**
 * Returns the single per-period descriptor that drives default-selection,
 * ValidityContent rendering, and MplCalendarSheet props. Start dates come from
 * the server-evaluated `startDates` window (§9); without one no start date is
 * offered.
 */
export const getValidityConfig = (
	category: TicketCategory,
	validity: IValidity,
	startDates: IStartWindow | undefined,
	today: string
): IValidityConfig => {
	if (category === 'short-trip') {
		return {
			mode: 'static',
			options: [],
			staticKeys: {
				description: 'ticket_detail.validity_short_trip_description',
				note: 'ticket_detail.validity_short_trip_note',
			},
			defaultStartDate: today, // backend forces today; enables the CTA
		};
	}

	const options =
		category === 'subscription'
			? buildMonthOptions(
					startDates,
					'ticket_detail.validity_subscription_from_month'
			  ) // no picker for yearly ticket
			: validity.period === 'day'
			? buildDailyOptions(startDates)
			: validity.period === 'week'
			? buildWeeklyOptions(startDates)
			: buildMonthOptions(
					startDates,
					'ticket_detail.validity_monthly_for_month'
			  ); // no picker for monthly ticket

	const picker =
		category === 'time-bound' &&
		(validity.period === 'day' || validity.period === 'week')
			? buildPickerConfig(validity.period, startDates)
			: undefined;

	return {
		mode: 'options',
		options,
		defaultStartDate: options[0]?.startDate ?? null,
		...(picker ? { picker } : {}),
	};
};
